import {
  type Dispatch,
  type PayloadAction,
  createSlice,
} from '@reduxjs/toolkit';
import type { TAppThunk } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type { TEmailPreferences } from '@timeedit/types';
import api from '../services/api.service';

type TEmailPreferencesState = IDefaultReducerState & {
  emailPreferences: Partial<TEmailPreferences>;
  updatedEmailPreferences: Partial<TEmailPreferences>;
};
type TEmailPreferencesPayload = PayloadAction<Partial<TEmailPreferences>>;

export const initialState: TEmailPreferencesState = {
  loading: false,
  hasErrors: false,
  emailPreferences: {},
  updatedEmailPreferences: {},
};

// Slice
const slice = createSlice({
  name: 'emailPreferences',
  initialState,
  reducers: {
    fetchEmailPreferencesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchEmailPreferencesSuccess: (
      state,
      { payload }: TEmailPreferencesPayload
    ) => {
      state.emailPreferences = payload;
      state.updatedEmailPreferences = payload;
      finishedLoadingSuccess(state);
    },
    fetchEmailPreferencesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setsUpdatedEmailPreferences: (
      state,
      { payload }: TEmailPreferencesPayload
    ) => {
      state.updatedEmailPreferences = payload;
    },
    updateEmailPreferencesRequest: (state) => {
      isLoadingRequest(state);
    },
    updateEmailPreferencesSuccess: (
      state,
      { payload }: TEmailPreferencesPayload
    ) => {
      state.emailPreferences = payload;
      state.updatedEmailPreferences = payload;
      finishedLoadingSuccess(state);
    },
    updateEmailPreferencesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    discardEmailPreferences: (state) => {
      state.updatedEmailPreferences = state.emailPreferences;
    },
  },
});

export default slice.reducer;

// Selectors
export const updatedEmailPreferencesSelector = (state) =>
  state.emailPreferences.updatedEmailPreferences;

// Actions
export const {
  fetchEmailPreferencesRequest,
  fetchEmailPreferencesSuccess,
  fetchEmailPreferencesFailure,
  setsUpdatedEmailPreferences,
  updateEmailPreferencesRequest,
  updateEmailPreferencesSuccess,
  updateEmailPreferencesFailure,
  discardEmailPreferences,
} = slice.actions;

export const fetchEmailPreferences =
  (): TAppThunk => async (dispatch: Dispatch) => {
    try {
      dispatch(fetchEmailPreferencesRequest());
      const emailPreferences = await api.get({
        endpoint: `/email-preferences`,
      });
      dispatch(fetchEmailPreferencesSuccess(emailPreferences));
    } catch (e) {
      dispatch(fetchEmailPreferencesFailure());
      return console.error(e);
    }
  };

export const setUpdatedEmailPreferences = createAppAsyncThunk<
  void,
  TEmailPreferences
>(
  'emailPreferences/setUpdatedEmailPreferences',
  async (emailPreferences, { dispatch }) => {
    dispatch(setsUpdatedEmailPreferences(emailPreferences));
  }
);

export const updateEmailPreferences = createAppAsyncThunk<
  void,
  TEmailPreferences
>(
  'emailPreferences/updateEmailPreferences',
  async (emailPreferences, { dispatch }) => {
    try {
      dispatch(updateEmailPreferencesRequest());
      const { ...emailPreferencesBody } = emailPreferences;
      const response = await api.patch({
        endpoint: `/email-preferences`,
        data: { ...emailPreferencesBody },
      });
      dispatch(updateEmailPreferencesSuccess(response));
    } catch (e) {
      dispatch(updateEmailPreferencesFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);
